import React from 'react';
import { useSEO } from '@/hooks/useSEO';
import { motion } from 'framer-motion';
import {
  Rocket,
  Heart,
  Users,
  Globe,
  GraduationCap,
  ArrowRight,
  Building2,
  Target,
  Coffee,
} from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

const whyJoinUs = [
  {
    title: 'Mission that matters',
    description: "We're democratizing career growth with AI coaching and personalized learning so everyone can level up.",
    icon: Target,
    gradient: 'from-primary/10 to-violet-500/10',
    iconBg: 'bg-primary/15 text-primary',
  },
  {
    title: 'Build with impact',
    description: "Ship product that directly improves people's careers—learning paths, nudges, and measurable outcomes.",
    icon: Rocket,
    gradient: 'from-emerald-500/10 to-teal-500/10',
    iconBg: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  },
  {
    title: 'Growth & learning',
    description: 'We practice what we preach: continuous learning, feedback, and career development for every team member.',
    icon: GraduationCap,
    gradient: 'from-amber-500/10 to-orange-500/10',
    iconBg: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  },
  {
    title: 'Inclusive culture',
    description: "Diverse, collaborative teams where everyone's voice counts and ideas are heard.",
    icon: Users,
    gradient: 'from-violet-500/10 to-fuchsia-500/10',
    iconBg: 'bg-violet-500/15 text-violet-600 dark:text-violet-400',
  },
  {
    title: 'Flexible & remote-friendly',
    description: 'Work from where you do your best—we value outcomes and wellbeing over face time.',
    icon: Globe,
    gradient: 'from-blue-500/10 to-cyan-500/10',
    iconBg: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  },
  {
    title: 'Move fast, stay human',
    description: 'High pace with a focus on sustainability, balance, and supporting each other.',
    icon: Coffee,
    gradient: 'from-rose-500/10 to-pink-500/10',
    iconBg: 'bg-rose-500/15 text-rose-600 dark:text-rose-400',
  },
];

const openRoles = [
  {
    title: 'Senior Full-Stack Engineer',
    team: 'Product Engineering',
    type: 'Full-time · Remote',
    description: 'Build the next generation of AI-powered learning and coaching experiences.',
  },
  {
    title: 'Product Designer',
    team: 'Design',
    type: 'Full-time · Remote',
    description: 'Shape UX for personalized learning paths, coach UI, and learner journeys.',
  },
  {
    title: 'AI / ML Engineer',
    team: 'AI & Learning',
    type: 'Full-time · Remote',
    description: 'Improve our coaching agent, personalization, and learning content systems.',
  },
];

export const CareersPage: React.FC = () => {
  useSEO({ title: 'Careers at Five AI - Join Our Team', description: 'Join Five AI and help democratize career growth with AI. We are building the future of personalized coaching and micro-learning. Based in Bangalore, India. See open roles.', path: '/careers' });

  return (
    <main id="main-content" className="min-h-screen bg-background" role="main" aria-label="Careers at Five AI">
      <Header />
      <div className="pt-16">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.18),transparent_50%)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.25),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_80%_20%,hsl(263_70%_50%/0.08),transparent)]" />
          <div className="container relative mx-auto px-4 py-24 sm:py-32">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8"
              >
                <Building2 className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Join the team</span>
              </motion.div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
                <span className="text-foreground">Careers at</span>
                <br />
                <span className="bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
                  Five AI
                </span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-4">
                We're building career transformation for everyone. Join us to make AI coaching and personalized learning accessible to all.
              </p>
              <p className="text-sm text-muted-foreground/80">
                Build with impact · Grow with us
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-4 sm:py-8">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-8">
              Why join us
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {whyJoinUs.map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
                  >
                    <Card
                      hover
                      className={`h-full p-6 sm:p-8 rounded-2xl border-2 bg-gradient-to-br ${item.gradient} hover:border-primary/40 transition-all duration-300`}
                    >
                      <div className={`w-11 h-11 rounded-xl ${item.iconBg} flex items-center justify-center mb-5 shrink-0`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-3">{item.title}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-4 sm:py-8">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-2">
              Open roles
            </h2>
            <p className="text-muted-foreground mb-8 max-w-xl">
              We're always looking for talented people. Don't see a fit? Email us at{' '}
              <a href="mailto:careers@fiveai.co" className="text-primary hover:underline font-medium">careers@fiveai.co</a>.
            </p>
            <div className="max-w-2xl space-y-4">
              {openRoles.map((role, i) => (
                <motion.div
                  key={role.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <Card hover className="p-6 sm:p-8 rounded-2xl border-2 hover:border-primary/40 transition-all duration-300">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">{role.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{role.team} · {role.type}</p>
                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{role.description}</p>
                      </div>
                      <Button asChild variant="outline" size="sm" className="shrink-0 border-primary/30 text-primary hover:bg-primary/10 rounded-xl">
                        <a href={`mailto:careers@fiveai.co?subject=Application: ${encodeURIComponent(role.title)}`}>
                          Apply
                        </a>
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative py-12 sm:py-16 border-t border-border">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.03] to-transparent dark:via-primary/[0.06]" />
          <div className="container relative mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center max-w-2xl mx-auto"
            >
              <Card className="rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-primary/10 to-violet-500/10 p-8 sm:p-10">
                <Heart className="w-10 h-10 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-2">Not sure where you fit?</h3>
                <p className="text-muted-foreground mb-6">
                  Send us your background and what you're excited about. We'd love to hear from you.
                </p>
                <Button asChild size="lg" className="rounded-xl gap-2">
                  <a href="mailto:careers@fiveai.co">
                    Email careers@fiveai.co
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </Button>
              </Card>
            </motion.div>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
};
